import { BaseHttpService } from '@shared-services/base-http-service';
import { BaseServiceOperationResult } from "@shared-results/BaseServiceOperationResult";
import { BehaviorSubject, NEVER, Observable, Observer, of, Subject, Subscription, timer } from "rxjs";
import { catchError, delay, repeat, retry, switchMap } from "rxjs/operators";

export abstract class BasePollingService<TResult extends BaseServiceOperationResult, TData> extends BaseHttpService {

  retryOnPollingErrorTime: number = 5010;
  retryOnErrorTime: number = 2500;
  private pollingIntervalDefault: number = 60000; // 60 seconds
  private pollingIntervalMinimum: number = 3000; // 3 seconds
  private pollingIntervalMaximum: number = 3600000; // 1 hour

  protected pollingEnabled = new BehaviorSubject<boolean>(false);
  private pollingDataSubject: Subject<TData> = null;
  private pollingDataHttpMethodObservable: Observable<TResult> = null;

  constructor(friendlyName: string) {
    super(friendlyName);
    // Set up subscriptions for polling
    this.pollingEnabled.pipe(
      switchMap(polling => {
        if (polling) {
          return of(null).pipe(
            // Wait for first polling interval to pass before starting
            delay(this.getPollingInterval()),
            switchMap(_ =>
              this.getPollingObservable().pipe(
                // Keep polling for data
                // Retry on error
                retry({
                  delay: (error, retryCount) => {
                    // Standard retry interval
                    let interval = this.getPollingRetryInterval();
                    if (this.retryBackoffEnabled()) {
                      // Calculate interval with backoff to avoid too many requests when there are errors
                      // Exponential backoff increases too quickly for our use cases i.e. continuous polling
                      //interval = Math.pow(2, retryCount - 1) * interval;
                      // Use simpler calculation to increase retry interval based on number of retries
                      interval = interval * retryCount;
                    }
                    return timer(this.getSanitisedPollingInterval(interval));
                  },
                  count: this.getPollingRetryAttempts()
                }),
                // Repeat after delay
                repeat({ delay: (count) => timer(this.getSanitisedPollingInterval(this.getPollingInterval())) }),
                catchError((error: any, caught: Observable<TResult>) => {
                  // Signal to inheriting class that an error has occurred
                  this.onPollingError(error);
                  return of(this.errorService?.handleOperationError<TResult>(`${this.friendlyName} polling`, this));
                })
              ))
          );
        } else {
          return NEVER;
        }
      })
    ).subscribe(
      (result: TResult) => {
        // Signal to inheriting class that data received
        if (result) {
          let pollingData: TData = this.onPollingResult(result);
          this.setPollingCachedData(pollingData);
        }
      }
    );
  }

  pollingStart(onlyTriggerIfChanged: boolean = false) {
    if (!onlyTriggerIfChanged || this.pollingEnabled.value !== true) {
      this.pollingEnabled.next(true);
    }
  }

  pollingStop(onlyTriggerIfChanged: boolean = false) {
    if (!onlyTriggerIfChanged || this.pollingEnabled.value !== false) {
      this.pollingEnabled.next(false);
    }
  }

  /**
   * Returns the latest polling data if we already have it, or requests it if we don't
   * @param observerOrNext
   */
  getPollingData(observerOrNext?: Partial<Observer<TData>> | ((value: TData) => void)): Subscription {
    let latestCachedPollingData: TData = this.getPollingCachedData();
    if (latestCachedPollingData) {
      // Already have, so return straight away
      let partialObserver: Partial<Observer<TData>> = this.getPartialObserver(observerOrNext);
      partialObserver?.next(latestCachedPollingData);
      partialObserver?.complete();
      // No subscription to return
      return null;
    }
    else {
      // Don't have yet, trigger a refresh      
      return this.refreshPollingData(observerOrNext);
    }
  }

  /**
   * Requests latest polling data and waits for response
   * @param observerOrNext
   */
  refreshPollingData(observerOrNext?: Partial<Observer<TData>> | ((value: TData) => void)): Subscription {
    // Check if signalling observable already exists
    if (!this.pollingDataSubject || this.pollingDataSubject?.closed) {
      // Observable doesn't exist yet, create
      this.pollingDataSubject = new Subject<TData>();
    }
    // Subscribe observer passed as parameter
    let subscription: Subscription = this.pollingDataSubject.subscribe(observerOrNext);

    if (this.pollingDataHttpMethodObservable) {
      // Refresh call already in progress
    }
    else {
      // Create new HTTP observable
      this.pollingDataHttpMethodObservable = this.getPollingObservable().pipe(
        // Retry on error
        retry({
          delay: (error, retryCount) => {
            if (this.pollingDataSubject?.observed) {
              // Still have subscription, retry
              return timer(this.retryOnErrorTime);
            }
            // No subscriptions, throw error to stop retrying
            throw new Error(`No subscribers left, stop retry`);
          }
        }),
      );
      // Subscribe to the observable to start it
      this.pollingDataHttpMethodObservable.subscribe({
        next: (result: TResult) => {
          // Signal to inheriting class that data received
          let pollingData: TData = this.onPollingResult(result);
          this.setPollingCachedData(pollingData);
          // Refresh completed successfully, so return
          this.pollingDataSubject?.next(pollingData);
          this.pollingDataSubject?.complete();
          // Clear signalling observable
          this.pollingDataSubject?.unsubscribe();
          this.pollingDataSubject = null;
        },
        error: (error: any) => {
          // Clear HTTP observable
          this.pollingDataHttpMethodObservable = null;
        },
        complete: () => {
          // Clear HTTP observable
          this.pollingDataHttpMethodObservable = null;
        }
      });
    }
    // Return the subscription
    return subscription;
  }

  private getSanitisedPollingInterval(pollingInterval: number): number {
    // Check that value acceptable
    if (typeof pollingInterval !== "number") {
      // Polling interval not valid, use default value
      pollingInterval = this.pollingIntervalDefault;
    } else if (pollingInterval < this.pollingIntervalMinimum) {
      // Polling interval less than minimum allowed, use minimum value
      pollingInterval = this.pollingIntervalMinimum;
    } else if (pollingInterval > this.pollingIntervalMaximum) {
      // Polling interval more than maximum allowed, use maximum value
      pollingInterval = this.pollingIntervalMaximum;
    }
    return pollingInterval;
  }

  protected abstract getPollingObservable(): Observable<TResult>;

  protected abstract getPollingInterval(): number;

  protected abstract onPollingError(error: any): void;

  protected abstract onPollingResult(result: TResult): TData;

  protected abstract getPollingCachedData(): TData;

  protected abstract setPollingCachedData(data: TData): void;

  protected getPollingRetryInterval(): number {
    return this.getPollingInterval();
  }

  protected getPollingRetryAttempts(): number {
    return Infinity;
  }

  protected retryBackoffEnabled(): boolean {
    return true;
  }

}
